import classnames from "classnames";
import { memo } from "react";
import { twMerge } from "tailwind-merge";

interface IProps {
  issueType?: "regular" | "city" | "country";
  setHoveredIssueType?: React.Dispatch<
    React.SetStateAction<"regular" | "city" | "country" | undefined>
  >;
  setIsIssueHovered?: React.Dispatch<React.SetStateAction<boolean>>;
  setIssueHoveredProps?: React.Dispatch<
    React.SetStateAction<{
      lat: number;
      lng: number;
    }>
  >;
  forceHoverIssue?: boolean;
  geoLocation?: {
    lat?: number | undefined;
    lng?: number | undefined;
  };
  header?: React.ReactNode | React.ReactNode[];
  subHeader?: React.ReactNode | React.ReactNode[];
  showSubHeaderDivider?: boolean;
  content?: React.ReactNode | React.ReactNode[];
  list?: React.ReactNode | React.ReactNode[];
  footer?: React.ReactNode | React.ReactNode[];
  collapse?: boolean;
  customClickEventsOnCardAreas?: {
    headerBlock?: () => void;
    contentBlock?: () => void;
    footerBlock?: () => void;
    dividersBlock?: () => void;
  };
  classNames?: {
    header?: string;
    subHeader?: string;
    content?: string;
    list?: string;
    footer?: string;
    contentInner?: string;
  };
  showContent?: boolean;
  customRef?: React.MutableRefObject<HTMLDivElement | null>;
  className?: string;
  shouldHover?: boolean;
  showHeaderDivider?: boolean;
  showFooterDivider?: boolean;
  defaultStyle?: boolean;
  tags?: React.ReactNode | React.ReactNode[];
  accentColor?: string;
}

function Card({
  header,
  content,
  issueType,
  setHoveredIssueType,
  setIsIssueHovered,
  setIssueHoveredProps,
  forceHoverIssue,
  shouldHover = true,
  list,
  customRef,
  geoLocation,
  footer,
  subHeader,
  classNames,
  customClickEventsOnCardAreas,
  collapse = false,
  className,
  showContent = true,
  showSubHeaderDivider = false,
  showHeaderDivider = true,
  showFooterDivider = true,
  defaultStyle = true,
  tags,
  accentColor,
}: IProps) {
  return (
    <div
      ref={customRef}
      onMouseOver={() => {
        setHoveredIssueType && setHoveredIssueType(issueType);
        setIsIssueHovered && setIsIssueHovered(true);
        geoLocation &&
          geoLocation.lng &&
          geoLocation.lat &&
          setIssueHoveredProps &&
          setIssueHoveredProps({
            lat: geoLocation?.lat,
            lng: geoLocation?.lng,
          });
      }}
      onMouseOut={() => {
        setHoveredIssueType && setHoveredIssueType(undefined);
        setIsIssueHovered && setIsIssueHovered(false);
        setIssueHoveredProps && setIssueHoveredProps({ lat: NaN, lng: NaN });
      }}
      className={twMerge(
        classnames(
          "rounded-xl relative flex flex-col gap-3 py-5 px-0 transition-all duration-300 ease-in-out min-h-[200px]",
          className
        )
      )}
      style={{
        background: defaultStyle ? '#FFFEF9' : 'transparent',
        border: defaultStyle ? '1px solid #E7E5E4' : 'none',
        boxShadow: forceHoverIssue && shouldHover
          ? '0 8px 30px -10px rgba(28, 25, 23, 0.2)'
          : (shouldHover && defaultStyle
            ? '0 2px 8px -2px rgba(28, 25, 23, 0.08)'
            : 'none'),
        fontFamily: "'Source Sans 3', sans-serif",
      }}
      onMouseEnter={(e) => {
        if (shouldHover && defaultStyle) {
          e.currentTarget.style.boxShadow = '0 8px 30px -10px rgba(28, 25, 23, 0.15)';
          e.currentTarget.style.transform = 'translateY(-2px)';
        }
      }}
      onMouseLeave={(e) => {
        if (shouldHover && defaultStyle) {
          e.currentTarget.style.boxShadow = '0 2px 8px -2px rgba(28, 25, 23, 0.08)';
          e.currentTarget.style.transform = 'translateY(0)';
        }
      }}
    >
      <div className="w-full flex flex-col gap-2">
        <div
          onClick={customClickEventsOnCardAreas?.headerBlock}
          className={twMerge(
            classnames(
              "flex flex-col gap-2 w-full px-6 shrink-0 justify-between",
              classNames?.header
            )
          )}
        >
          {header}
        </div>
        {showSubHeaderDivider && (
          <div
            onClick={customClickEventsOnCardAreas?.dividersBlock}
            className={classnames(
              {
                "maxLg:h-0 maxLg:hidden": collapse,
              },
              "w-full h-[1px]"
            )}
            style={{ borderBottom: '1px solid #E7E5E4' }}
          />
        )}
        {subHeader && (
          <div
            onClick={customClickEventsOnCardAreas?.headerBlock}
            className={classnames(
              "flex  flex-col gap-2 w-full  px-6   shrink-0 justify-between",
              classNames?.subHeader
            )}
          >
            {subHeader}
          </div>
        )}
        {showHeaderDivider && (
          <div
            onClick={customClickEventsOnCardAreas?.dividersBlock}
            className={classnames(
              {
                "maxLg:h-0 maxLg:hidden": collapse,
              },
              "w-full h-[1px]"
            )}
            style={{ borderBottom: '1px solid #E7E5E4' }}
          />
        )}
        {showContent && (
          <div
            onClick={customClickEventsOnCardAreas?.contentBlock}
            className={classnames(
              "content flex w-full flex-col gap-1 px-6 flex-grow justify-between transition-all duration-300 ease-in-out",
              { "h-0 ": collapse },
              classNames?.content
            )}
          >
            <div
              className={classnames(
                "leading-5 h-full text-sm  font-normal text-gray-500 transition-all duration-300 ease-in-out ",
                { "h-0 hidden": collapse },
                classNames?.contentInner
              )}
            >
              {content}
            </div>
            {!!list && (
              <div
                className={classnames(
                  "flex gap-4 transition-all  duration-300 ease-in-out",
                  { "h-0 hidden": collapse },
                  classNames?.list
                )}
              >
                {list}
              </div>
            )}
          </div>
        )}
        {showFooterDivider && (
          <div
            onClick={customClickEventsOnCardAreas?.dividersBlock}
            className={classnames("w-full", {
              "h-0 hidden": collapse,
              "h-[1px]": !collapse,
            })}
            style={{ borderBottom: '1px solid #E7E5E4' }}
          />
        )}
      </div>

      {footer && (
        <div
          onClick={customClickEventsOnCardAreas?.footerBlock}
          className={classnames(
            "footer w-full  flex justify-between  items-center px-6 transition-all duration-300 ease-in-out  ",
            { "h-0 hidden": collapse },
            classNames?.footer
          )}
        >
          {footer}
        </div>
      )}

      {tags && <>{tags}</>}
    </div>
  );
}

export default memo(Card);
